import {
  promiseSnapshotStack,
  promiseStackFrames,
  trimPromiseCreationStack,
} from "@/lib/errors/enhanced-promise"
import { mtLocation } from "@/lib/mt-location"
import { reportUnexpectedError } from "@/lib/report-unexpected-error"

// Everything in this file supports one thing: the "unhandledrejection"
// listener registered during Next.js app initialization. The listener reports
// uncaught promise rejections to Honeybadger, enriched with the stack trace
// recorded when the promise was created (see
// src/lib/errors/enhanced-promise.ts for how that recording works and why).
// The property names here must match the ones enhanced-promise.ts writes.
const PROMISE_SNAPSHOT = "__mt_promise_snapshot"
const PROMISE_CURRENT_SCRIPT = "__mt_current_script"
const PROMISE_STACK = "__promise_stack"
// URL schemes a rejection can legitimately originate from. Anything else
// (data:, blob:, about:, custom app schemes) tells us nothing about who owns
// the code, so it classifies as "unknown" rather than "external".
const EXTERNAL_PROMISE_PROTOCOLS = new Set([
  "http:",
  "https:",
  "chrome-extension:",
  "moz-extension:",
  "safari-extension:",
  "safari-web-extension:",
  "webkit-masked-url:",
])

// Who owns the code that created the rejecting promise: "owned" is our own
// bundle, "external" is someone else's script (extension, injected script,
// third-party host), "unknown" means there was not enough evidence to say.
type PromiseOrigin = "owned" | "external" | "unknown"
type UnhandledRejectionContext = Record<
  string,
  string | number | boolean | null
>

// The try/catch wrappers on these small helpers are not decoration: the
// rejection value can be anything, including hostile objects from other
// scripts — cross-origin proxies whose property reads throw, getters that
// throw on purpose. An exception inside an error reporter would lose the
// report, so these readers treat inaccessible diagnostics as unavailable.

// The plain object RTK Query rejects with (see base-query.ts serializeError):
// a message plus, for HTTP failures, the request that failed.
function isSerializedError(reason: unknown): reason is {
  message: string
  requestPath?: unknown
  requestMethod?: unknown
  status?: unknown
} {
  try {
    return (
      typeof reason === "object" &&
      reason !== null &&
      typeof Reflect.get(reason, "message") === "string"
    )
  } catch {
    return false
  }
}

function isError(reason: unknown): reason is Error {
  try {
    return reason instanceof Error
  } catch {
    return false
  }
}

function readStack(value: Error): string | null {
  try {
    const stack = Reflect.get(value, "stack")
    return typeof stack === "string" ? stack : null
  } catch {
    return null
  }
}

// The stack trace from where the rejecting promise was created. Preferred
// source: the snapshot enhanced-promise.ts stored on the promise object
// itself (works for any rejection value). Fallback: the `__promise_stack`
// property on an Error rejection value, which survives paths where the
// promise object we receive isn't the one that recorded the snapshot.
function creationStackFor(promise: unknown, reason: unknown): string | null {
  if (
    promise !== null &&
    (typeof promise === "object" || typeof promise === "function")
  ) {
    try {
      const snapshot = Reflect.get(promise, PROMISE_SNAPSHOT)
      const snapshotStack = promiseSnapshotStack(snapshot)
      if (snapshotStack !== null) {
        return trimPromiseCreationStack(snapshotStack)
      }
    } catch {
      // Inaccessible snapshots leave the Error-side evidence available.
    }
  }

  if (isError(reason)) {
    try {
      const stack = Reflect.get(reason, PROMISE_STACK)
      return typeof stack === "string" ? trimPromiseCreationStack(stack) : null
    } catch {
      return null
    }
  }

  return null
}

// The URL of the <script> that was executing when the promise was created,
// if enhanced-promise.ts recorded one. Often names an extension or injected
// script directly when the stack cannot.
function currentScriptFor(promise: unknown): string | null {
  if (
    promise === null ||
    (typeof promise !== "object" && typeof promise !== "function")
  ) {
    return null
  }

  try {
    const scriptSource = Reflect.get(promise, PROMISE_CURRENT_SCRIPT)
    return typeof scriptSource === "string" && scriptSource.length > 0
      ? scriptSource
      : null
  } catch {
    return null
  }
}

// True if the Error carries a stack with at least one usable frame. Some
// rejection values are Error objects in name only — stack missing, empty, or
// containing nothing but frames from the promise instrumentation itself.
function hasRealStack(error: Error): boolean {
  const stack = trimPromiseCreationStack(readStack(error))
  return stack !== null && promiseStackFrames(stack).length > 0
}

// Pulls the source URL out of one stack-trace frame line: either a full URL
// (http(s) or an extension scheme) or, for relative paths, our own Next.js
// asset path starting at "/_next/".
function frameSource(frame: string | null): string | null {
  if (frame === null) {
    return null
  }

  const source = frame.match(
    /(?:https?:|(?:chrome|moz|safari)-extension:|safari-web-extension:|webkit-masked-url:)\/\/[^\s)]+/
  )?.[0]
  if (source) {
    return source
  }

  const nextAsset = frame.indexOf("/_next/")
  return nextAsset === -1 ? null : frame.slice(nextAsset)
}

// Decides who owns the code that created the promise. The first frame of the
// creation stack is where `new Promise(...)` ran; its URL (or, failing that,
// the recorded <script> URL) is the verdict: our own origin or a "/_next/"
// asset means our code ("owned"); a well-formed URL anywhere else — another
// site, an extension — means someone else's code ("external"); no usable
// evidence means "unknown". This classification is the field the team
// filters on in Honeybadger, so leaning toward "unknown" beats guessing.
function promiseOrigin(
  creationStack: string | null,
  currentScript: string | null
): PromiseOrigin {
  const firstFrame =
    creationStack === null
      ? null
      : (promiseStackFrames(creationStack)[0] ?? null)
  const source = frameSource(firstFrame) ?? currentScript
  if (source === null) {
    return "unknown"
  }
  if (source.startsWith("/_next/")) {
    return "owned"
  }

  try {
    const url = new URL(source, mtLocation.origin)
    if (
      url.origin === mtLocation.origin ||
      url.pathname.startsWith("/_next/")
    ) {
      return "owned"
    }

    return EXTERNAL_PROMISE_PROTOCOLS.has(url.protocol) ? "external" : "unknown"
  } catch {
    return "unknown"
  }
}

// The reason* helpers below describe a non-Error rejection value for the
// Honeybadger report: its typeof, its constructor name, its first own
// property names, and its string form. When something rejects with a bare
// object or string, these four fields are usually enough to recognize which
// library or script produced it.

function reasonConstructor(reason: unknown): string | null {
  if (reason === null || reason === undefined) {
    return null
  }

  try {
    const constructor = Reflect.get(Object(reason), "constructor")
    const name = Reflect.get(constructor, "name")
    return typeof name === "string" ? name : null
  } catch {
    return null
  }
}

function reasonOwnKeys(reason: unknown): string {
  if (
    reason === null ||
    (typeof reason !== "object" && typeof reason !== "function")
  ) {
    return ""
  }

  try {
    return Reflect.ownKeys(reason)
      .slice(0, 20)
      .map((key) => String(key))
      .join(",")
      .slice(0, 200)
  } catch {
    return ""
  }
}

function reasonString(reason: unknown): string {
  try {
    return String(reason).slice(0, 200)
  } catch {
    return "Unable to stringify rejection reason"
  }
}

function reasonContext(reason: unknown): UnhandledRejectionContext {
  const context: UnhandledRejectionContext = {
    reason_constructor: reasonConstructor(reason),
    reason_own_keys: reasonOwnKeys(reason),
    reason_string: reasonString(reason),
    reason_typeof: typeof reason,
  }

  try {
    if (isSerializedError(reason)) {
      for (const key of ["requestPath", "requestMethod", "status"] as const) {
        const value = reason[key]
        if (typeof value === "string" || typeof value === "number") {
          context[`reason_${key}`] = value
        }
      }
    }
    if (typeof DOMException !== "undefined" && reason instanceof DOMException) {
      const code = Reflect.get(reason, "code")
      context.reason_dom_exception_name = reason.name
      if (typeof code === "number") {
        context.reason_dom_exception_code = code
      }
    }
  } catch {
    return context
  }

  return context
}

// Honeybadger needs an Error object to report. If the rejection value is
// one, use it as-is (identity preserved — the notice carries the real error).
// Otherwise wrap its string form in a new Error and remember that we did,
// because the listener treats wrapped values differently (their stack is
// this listener, not the failure).
function normalizedRejection(reason: unknown): {
  error: Error
  reasonIsError: boolean
} {
  try {
    if (reason instanceof Error) {
      return { error: reason, reasonIsError: true }
    }

    // RTK Query rejects with a serialized plain object (see base-query.ts), so
    // String() would give "[object Object]" and lose the failure.
    if (isSerializedError(reason)) {
      return { error: new Error(reason.message), reasonIsError: false }
    }

    return { error: new Error(String(reason)), reasonIsError: false }
  } catch {
    return {
      error: new Error("Unable to convert thrown value to an Error"),
      reasonIsError: false,
    }
  }
}

export function reportUnhandledRejection(event: PromiseRejectionEvent): void {
  // The app's one owner for uncaught promise rejections: builds the
  // Honeybadger report.
  const { error, reasonIsError } = normalizedRejection(event.reason)
  // HB 134222605: injected Safari events can lack a valid promise; foreign getters may also throw.
  let promise: unknown
  try {
    promise = event.promise
  } catch {
    promise = undefined
  }
  const creationStack = creationStackFor(promise, event.reason)
  const currentScript = currentScriptFor(promise)
  const context: UnhandledRejectionContext = {
    promise_current_script: currentScript,
    promise_origin: promiseOrigin(creationStack, currentScript),
    unhandledPromiseRejection: true,
  }

  if (creationStack !== null) {
    context.promise_stack = creationStack
  }
  if (!reasonIsError) {
    Object.assign(context, reasonContext(event.reason))
  }
  // Best-evidence rule for the notice's main stack. Honeybadger groups
  // faults and applies source maps using that stack, so it should be the
  // truest one available. A real stack from the rejected Error shows
  // where the failure happened — better than where the promise was
  // created — so it stays. Only when there is no usable failure stack
  // (a non-Error rejection, or an Error with an empty stack: the notices
  // that used to arrive blank) does the creation stack take its place.
  if ((!reasonIsError || !hasRealStack(error)) && creationStack !== null) {
    error.stack = creationStack
  }

  reportUnexpectedError(error, "_app-unhandled-rejection", context)
}
